import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import { Miko } from "../components/miko/Miko";
import { Bell } from "../components/omikuji/Bell";
import { KujiBox } from "../components/omikuji/KujiBox";
import { OmikujiCard } from "../components/omikuji/OmikujiCard";
import { useSound } from "../hooks/useSound";
import { useVibration, VibrationPatterns } from "../hooks/useVibration";
import { useShake } from "../hooks/useShake";
import { useGyro } from "../hooks/useGyro";
import { drawOmikuji, type DrawResult } from "../lib/omikuji";
import {
  appendDraw,
  ensurePair,
  getPairs,
  getProfile,
  saveProfile,
} from "../lib/storage";

/**
 * §1.3 初回ペア儀式 — 12ステップシーケンス (約45秒)
 *
 * 自動進行ステップと、ユーザー操作で進めるステップが混在。
 *  1. summon       (4s 自動)   二人の名前 in 金箔
 *  2. approach     (3s 自動)   境内移動 (本殿へカメラパン)
 *  3. miko         (3s 自動)   巫女が籤箱を持って登場
 *  4. shake        (待機)      鈴を振る → useShake.completed
 *  5. boxes        (3s 自動)   籤箱が二つ並ぶ
 *  6. tilt         (待機)      ジャイロ or タップで籤箱を傾ける
 *  7. stick        (3s 自動)   棒がカランと出る
 *  8. drawing      (5s 自動)   巫女が筒から取り出す
 *  9. countdown    (4s 自動)   三・二・一
 * 10. open         (3s 自動)   札がひらりと開く
 * 11. result       (5s 自動)   結果テキスト読み上げ
 * 12. collect      (3s 自動)   コレクションに追加
 *  → /home へ
 */

type Step =
  | "summon"
  | "approach"
  | "miko"
  | "shake"
  | "boxes"
  | "tilt"
  | "stick"
  | "drawing"
  | "countdown"
  | "open"
  | "result"
  | "collect";

const ORDER: Step[] = [
  "summon",
  "approach",
  "miko",
  "shake",
  "boxes",
  "tilt",
  "stick",
  "drawing",
  "countdown",
  "open",
  "result",
  "collect",
];

type State = { step: Step; cardState: "closed" | "opening" | "opened" };

function reducer(
  state: State,
  action:
    | { type: "advance" }
    | { type: "set"; step: Step }
    | { type: "card"; v: State["cardState"] },
): State {
  if (action.type === "advance") {
    const idx = ORDER.indexOf(state.step);
    return { ...state, step: ORDER[Math.min(idx + 1, ORDER.length - 1)] };
  }
  if (action.type === "set") return { ...state, step: action.step };
  return { ...state, cardState: action.v };
}

const AUTO_DURATIONS: Partial<Record<Step, number>> = {
  summon: 4000,
  approach: 3000,
  miko: 3000,
  boxes: 3000,
  stick: 2500,
  drawing: 5000,
  countdown: 3500,
  open: 3000,
  result: 5500,
  collect: 3000,
};

export function FirstPairRitual() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();
  const reduce = useReducedMotion();

  // プロファイル + 招待ペアから動的に取得 (フォールバックは「あなた」「ゆう」)
  const { userName, pairId, partnerName } = useMemo(() => {
    const profile = getProfile();
    const pairs = getPairs();
    const invitedPair =
      pairs.find((p) => p.pairId.startsWith("inv_")) ?? pairs[0];
    return {
      userName: profile?.displayName ?? "あなた",
      pairId: invitedPair?.pairId ?? "seed_yu",
      partnerName: invitedPair?.partnerName ?? "ゆう",
    };
  }, []);

  // 結果は儀式開始時に確定 (再描画で抽選し直さない)
  const result = useMemo<DrawResult>(
    () => drawOmikuji({ partnerName }),
    [partnerName],
  );
  const partnerResult = useMemo<DrawResult>(
    () => drawOmikuji({ partnerName: userName }),
    [userName],
  );

  const savedRef = useRef(false);

  const [state, dispatch] = useReducer(reducer, {
    step: "summon",
    cardState: "closed",
  });
  const startedRef = useRef(false);
  const [started, setStarted] = useState(false);

  const shake = useShake({ goal: 6 });
  const { tilt, requestPermission: requestGyro } = useGyro(
    state.step === "tilt",
  );
  const [tapTilts, setTapTilts] = useState(0); // タップで傾ける用 (PCフォールバック)

  // shake 完了 → 自動で次へ (boxes)
  useEffect(() => {
    if (state.step === "shake" && shake.completed) {
      play("kirari");
      vibrate(VibrationPatterns.bellShake);
      const id = window.setTimeout(() => dispatch({ type: "advance" }), 400);
      return () => clearTimeout(id);
    }
  }, [state.step, shake.completed, play, vibrate]);

  // tilt 完了判定 (ジャイロ角 or タップ蓄積)
  const tiltCompleted =
    Math.abs(tilt.gamma) > 25 || Math.abs(tilt.beta) > 30 || tapTilts >= 4;
  useEffect(() => {
    if (state.step === "tilt" && tiltCompleted) {
      play("woodTap");
      vibrate(VibrationPatterns.woodTap);
      const id = window.setTimeout(() => dispatch({ type: "advance" }), 400);
      return () => clearTimeout(id);
    }
  }, [state.step, tiltCompleted, play, vibrate]);

  // 自動進行ステップ
  useEffect(() => {
    const dur = reduce ? 600 : AUTO_DURATIONS[state.step];
    if (!dur) return;
    const id = window.setTimeout(() => dispatch({ type: "advance" }), dur);
    return () => clearTimeout(id);
  }, [state.step, reduce]);

  // ステップ毎の効果音 trigger
  useEffect(() => {
    switch (state.step) {
      case "summon":
        play("kirari");
        break;
      case "miko":
        vibrate(20);
        break;
      case "boxes":
        play("woodTap");
        break;
      case "stick":
        play("woodTap");
        vibrate(VibrationPatterns.woodTap);
        break;
      case "countdown":
        // 1秒ごとに鼓動風
        for (let i = 0; i < 3; i++) {
          window.setTimeout(() => play("bellTap"), i * 1000);
        }
        break;
      case "open":
        dispatch({ type: "card", v: "opening" });
        play("cardOpen");
        vibrate(VibrationPatterns.cardOpen);
        window.setTimeout(() => dispatch({ type: "card", v: "opened" }), 600);
        if (
          result.rarity === "dai_kichi" ||
          result.rarity === "tokudai_kichi"
        ) {
          window.setTimeout(() => {
            play("taikoDon");
            vibrate(VibrationPatterns.daiKichi);
          }, 800);
        }
        break;
      case "collect":
        play("kirari");
        if (!savedRef.current) {
          savedRef.current = true;
          // 初回ペアを ensure (招待フローで作成済の想定だが安全に)
          ensurePair({ pairId, partnerName });
          appendDraw({
            mode: "pair",
            isFirstPair: true,
            pairId,
            rarity: result.rarity,
            bodyText: result.bodyText,
            bodyTextEn: result.bodyTextEn,
            themeAuto: result.themeAuto,
            partnerName,
          });
          saveProfile({ isUnlocked: true });
        }
        break;
    }
    // 各ステップ初回だけ鳴らす
  }, [state.step, play, vibrate, result.rarity]);

  // collect 後 → home へ
  useEffect(() => {
    if (state.step === "collect") {
      const id = window.setTimeout(
        () => navigate("/home"),
        AUTO_DURATIONS.collect,
      );
      return () => clearTimeout(id);
    }
  }, [state.step, navigate]);

  function startRitual() {
    if (startedRef.current) return;
    startedRef.current = true;
    setStarted(true);
    unlock();
    void requestGyro(); // iOS: ジャイロ permission リクエスト (ユーザージェスチャ内で)
    void shake.requestPermission();
  }

  // ===== Render =====

  return (
    <main className="relative min-h-dvh overflow-hidden bg-[var(--color-night-deep)] text-[var(--color-washi)] select-none">
      {/* 背景: 境内 */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 80%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 50%, #1a0c12 100%)",
        }}
      />
      {/* 桜は miko 以降 */}
      {(
        [
          "miko",
          "shake",
          "boxes",
          "tilt",
          "stick",
          "drawing",
          "countdown",
          "open",
          "result",
          "collect",
        ] as Step[]
      ).includes(state.step) && <Sakura count={12} />}

      {/* スキップ (デバッグ・離脱時用) */}
      {started && state.step !== "collect" && (
        <button
          onClick={() => navigate("/home")}
          className="absolute top-4 right-4 z-50 text-[10px] tracking-widest text-[var(--color-gold-light)] opacity-50 hover:opacity-100"
        >
          {t("ritual.skip")} →
        </button>
      )}

      {/* ステージ */}
      <div className="relative z-10 min-h-dvh flex flex-col items-center justify-center px-4 py-8">
        {/* ヒントテキスト (上部) */}
        <AnimatePresence mode="wait">
          {state.step !== "summon" &&
            state.step !== "result" &&
            state.step !== "collect" && (
              <motion.p
                key={state.step + "-hint"}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 0.85, y: 0 }}
                exit={{ opacity: 0 }}
                className="absolute top-[8%] inset-x-0 text-center text-sm tracking-[0.2em] text-[var(--color-gold-light)]"
                style={{ fontFamily: "var(--font-fude)" }}
              >
                {hintFor(state.step, t)}
              </motion.p>
            )}
        </AnimatePresence>

        {/* === Step 1: summon === */}
        <AnimatePresence>
          {state.step === "summon" && (
            <motion.div
              key="summon"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.4 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="text-center"
            >
              <p
                className="text-5xl tracking-[0.4em]"
                style={{
                  fontFamily: "var(--font-fude)",
                  background:
                    "linear-gradient(180deg, #fffacd 0%, #e6cc88 50%, #8a6b2c 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 0 32px rgba(255,200,80,0.6)",
                }}
              >
                {t("ritual.summon", { a: userName, b: partnerName })}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Step 2: approach === */}
        <AnimatePresence>
          {state.step === "approach" && (
            <motion.div
              key="approach"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1.4 }}
              exit={{ opacity: 0, scale: 2.2 }}
              transition={{ duration: 2.6, ease: "easeIn" }}
              className="absolute inset-0 flex items-center justify-center text-[14rem]"
              style={{ color: "rgba(159,29,53,0.5)", filter: "blur(2px)" }}
              aria-hidden
            >
              ⛩
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Step 3+: miko === */}
        <AnimatePresence>
          {(
            ["miko", "shake", "boxes", "tilt", "stick", "drawing"] as Step[]
          ).includes(state.step) && (
            <motion.div
              key="miko"
              initial={{ opacity: 0, y: 80, scale: 0.9 }}
              animate={{
                opacity: state.step === "miko" ? 1 : 0.6,
                y: 0,
                scale: 1,
              }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 1.0, ease: "easeOut" }}
              className="absolute bottom-[50%] translate-y-1/2"
            >
              <Miko size={state.step === "miko" ? 220 : 140} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Step 4: shake === */}
        <AnimatePresence>
          {state.step === "shake" && (
            <motion.div
              key="shake"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.2 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center gap-4"
            >
              <Bell
                intensity={shake.intensity}
                onTap={() => {
                  shake.tap();
                  play("bellTap");
                  vibrate(VibrationPatterns.bellTap);
                }}
                size={220}
                label={t("ritual.shakeBell")}
              />
              <div className="w-40 h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-[var(--color-gold)]"
                  animate={{ width: `${shake.intensity * 100}%` }}
                  transition={{ ease: "easeOut" }}
                />
              </div>
              <p className="text-[10px] opacity-60 tracking-widest">
                {t("ritual.shakeHint")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Step 5: boxes appear === */}
        <AnimatePresence>
          {(["boxes", "tilt", "stick"] as Step[]).includes(state.step) && (
            <motion.div
              key="boxes"
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9 }}
              className="flex gap-8 items-end"
            >
              <KujiBox
                tilt={state.step === "tilt" ? tilt.gamma + tapTilts * 8 : 0}
                stickOut={state.step === "stick"}
                size={140}
                label="あなたの籤箱"
              />
              <KujiBox
                tilt={state.step === "tilt" ? -(tilt.gamma + tapTilts * 8) : 0}
                stickOut={state.step === "stick"}
                size={140}
                label="ゆうの籤箱"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Step 6: tilt overlay (タップで傾ける button) === */}
        {state.step === "tilt" && (
          <button
            onClick={() => setTapTilts((n) => n + 1)}
            aria-label={t("ritual.tiltBox")}
            className="absolute bottom-[18%] inset-x-0 mx-auto w-44 py-3 rounded-full text-xs tracking-widest text-[var(--color-washi)] border border-[var(--color-gold)]/50 bg-[var(--color-night)]/60 backdrop-blur"
          >
            {t("ritual.tiltHint")} ({Math.min(tapTilts, 4)}/4)
          </button>
        )}

        {/* === Step 8: drawing (巫女が取り出す) === */}
        <AnimatePresence>
          {state.step === "drawing" && (
            <motion.div
              key="drawing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-6"
            >
              <Miko size={200} />
              <p
                className="text-base tracking-widest text-[var(--color-gold-light)]"
                style={{ fontFamily: "var(--font-fude)" }}
              >
                {t("ritual.drawing")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Step 9: countdown === */}
        <AnimatePresence>
          {state.step === "countdown" && (
            <Countdown
              onTick={(n) => {
                if (n === 0) {
                  // open に進む前の最終振動
                  vibrate(40);
                }
              }}
            />
          )}
        </AnimatePresence>

        {/* === Step 10-11: cards === */}
        <AnimatePresence>
          {(["open", "result", "collect"] as Step[]).includes(state.step) && (
            <motion.div
              key="cards"
              initial={{ opacity: 0, y: 60 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: state.step === "collect" ? 0.5 : 1,
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="flex gap-3 items-center"
            >
              <OmikujiCard
                rarity={result.rarity}
                body={result.bodyText}
                state={state.cardState}
                position="left"
                size={150}
                ownerLabel={userName}
              />
              <OmikujiCard
                rarity={partnerResult.rarity}
                body={partnerResult.bodyText}
                state={state.cardState}
                position="right"
                size={150}
                ownerLabel={partnerName}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Step 12: collect notice === */}
        <AnimatePresence>
          {state.step === "collect" && (
            <motion.div
              key="collect"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="absolute bottom-[15%] inset-x-0 text-center"
            >
              <p
                className="text-sm tracking-widest text-[var(--color-gold-light)]"
                style={{ fontFamily: "var(--font-fude)" }}
              >
                ✦ {t("ritual.added")} ✦
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* === 開始オーバーレイ === */}
      <AnimatePresence>
        {!started && (
          <motion.button
            key="start-overlay"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={startRitual}
            className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-[var(--color-night-deep)]"
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2.4, repeat: Infinity }}
              className="text-7xl text-[var(--color-gold)]"
              style={{ textShadow: "0 0 32px rgba(201,169,97,0.7)" }}
            >
              ⛩
            </motion.div>
            <p
              className="text-lg tracking-[0.3em] text-[var(--color-washi-warm)]"
              style={{ fontFamily: "var(--font-fude)" }}
            >
              初回の儀
            </p>
            <p className="text-[10px] text-[var(--color-gold-light)] opacity-70 tracking-widest">
              TAP TO BEGIN
            </p>
          </motion.button>
        )}
      </AnimatePresence>
    </main>
  );
}

function hintFor(step: Step, t: (k: string) => string): string {
  switch (step) {
    case "approach":
      return t("ritual.approachShrine");
    case "miko":
      return t("ritual.mikoArrives");
    case "shake":
      return t("ritual.shakeBell");
    case "boxes":
      return t("ritual.boxAppears");
    case "tilt":
      return t("ritual.tiltBox");
    case "stick":
      return t("ritual.stickOut");
    case "drawing":
      return t("ritual.drawing");
    case "open":
      return t("ritual.open");
    default:
      return "";
  }
}

function Countdown({ onTick }: { onTick?: (n: number) => void }) {
  const [n, setN] = useState(3);
  useEffect(() => {
    onTick?.(n);
    if (n <= 0) return;
    const id = window.setTimeout(() => setN(n - 1), 1000);
    return () => clearTimeout(id);
  }, [n, onTick]);
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={n}
        initial={{ opacity: 0, scale: 2 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.4 }}
        transition={{ duration: 0.6 }}
        className="text-9xl"
        style={{
          fontFamily: "var(--font-fude)",
          color: "var(--color-gold-light)",
          textShadow: "0 0 32px rgba(201,169,97,0.6)",
        }}
      >
        {n > 0 ? ["", "一", "二", "三"][n] : "✦"}
      </motion.div>
    </AnimatePresence>
  );
}
